// Supabase-backed data client: entities, auth, integrations, rpc, and
// realtime-function-invoke surfaces the whole app calls through. v1 --
// iterate against real call sites; see KNOWN GAPS at bottom.
import { supabase } from './supabaseClient';

// Entity name -> { table, cols } where `cols` are the strongly-typed
// columns. Anything not in `cols` lives in the `data` jsonb column.
const TABLES = {
  DriverProfile: { table: 'drivers', cols: ['id','carrier_id','user_id','full_name','user_email','phone','driver_type','employment_status','hire_date','termination_date','dq_purge_eligible_at','truck_vin','trailer_vin','created_at','updated_at'] },
  Asset:        { table: 'assets', cols: ['id','carrier_id','unit_number','asset_type','vin','assigned_driver_email','registration_expiration','plate_expiration','annual_inspection_due','current_odometer','is_active','created_at','updated_at'] },
  StaffProfile: { table: 'staff_profiles', cols: ['id','carrier_id','user_id','user_email','staff_role','is_active','created_at','updated_at'] },
  CompanySettings: { table: 'company_settings', cols: ['id','carrier_id','key','value','label','created_at','updated_at'] },
  Contact:      { table: 'contacts', cols: ['id','carrier_id','name','email','parent_contact_id','created_at','updated_at'] },
  Expense:      { table: 'expenses', cols: ['id','carrier_id','amount','category','expense_date','created_at','updated_at'] },
  FuelAdvance:  { table: 'fuel_advances', cols: ['id','carrier_id','load_id','amount','status','approved_date','advance_type','created_at','updated_at'] },
  IFTAMileageLog: { table: 'ifta_mileage_logs', cols: ['id','carrier_id','load_id','miles','jurisdiction','log_date','created_at','updated_at'] },
  Invoice:      { table: 'invoices', cols: ['id','carrier_id','load_id','amount','status','created_at','updated_at'] },
  Load:         { table: 'loads', cols: ['id','carrier_id','load_number','status','assigned_driver_email','assignment_status','rate','line_haul_rate','pickup_date','delivery_date','shipper_contact_id','consignee_contact_id','customer_contact_id','created_by','created_at','updated_at'] },
  // No `data` jsonb column on this table (high-frequency GPS breadcrumb
  // rows) -- noData tells toRow() not to send one, or PostgREST rejects
  // every insert as an unknown column.
  LoadLocationPing: { table: 'load_location_pings', cols: ['id','carrier_id','load_id','driver_email','lat','lng','recorded_at'], noData: true },
  LoadMessage:  { table: 'load_messages', cols: ['id','carrier_id','load_id','created_at','updated_at'] },
  MaintenanceRecord: { table: 'maintenance_records', cols: ['id','carrier_id','asset_id','cost','service_date','created_at','updated_at'] },
  Settlement:   { table: 'settlements', cols: ['id','carrier_id','load_id','load_number','driver_email','status','is_finalized','gross_revenue','carrier_take_amount','net_driver_pay','final_net_pay','settlement_date','finalized_date','created_at','updated_at'] },
  SettlementLineItem: { table: 'settlement_line_items', cols: ['id','carrier_id','settlement_id','amount','created_at','updated_at'] },
  Invitation:   { table: 'invitations', cols: ['id','carrier_id','invited_email','invited_role','token','status','created_by','accepted_by','expires_at','accepted_at','created_at','updated_at'] },
  StaffW4Form:  { table: 'staff_w4_forms', cols: ['id','carrier_id','user_id','tax_year','signature','signed_at','created_at','updated_at'] },
  Trip:         { table: 'trips', cols: ['id','carrier_id','trip_number','trip_type','status','assigned_driver_email','assigned_driver_name','truck_id','trailer_id','origin','destination','pickup_date','delivery_date','total_miles','dispatcher_user_id','created_at','updated_at'] },
  TripLoad:     { table: 'trip_loads', cols: ['id','carrier_id','trip_id','load_id','leg_sequence','created_at','updated_at'] },
  InvoiceLoad:  { table: 'invoice_loads', cols: ['id','carrier_id','invoice_id','load_id','created_at','updated_at'] },
};

const ALIAS = { created_date: 'created_at', updated_date: 'updated_at' };
const DB_MANAGED = new Set(['id','carrier_id','created_at','updated_at','created_date','updated_date','dq_purge_eligible_at']);

let _carrierId; // cached for this session
async function carrierId() {
  if (_carrierId) return _carrierId;
  const { data } = await supabase.from('memberships')
    .select('carrier_id').eq('status', 'active').limit(1).maybeSingle();
  _carrierId = data?.carrier_id || null;
  return _carrierId;
}

// DB row -> flat object the app expects (typed cols win over data; aliases)
function fromRow(cols, row) {
  if (!row) return row;
  const flat = { ...(row.data || {}) };
  for (const c of cols) if (c !== 'data') flat[c] = row[c];
  flat.created_date = row.created_at;
  flat.updated_date = row.updated_at;
  return flat;
}

// flat app object -> { typed cols..., data: {rest} }, carrier stamped
async function toRow(cols, obj, { isCreate, noData }) {
  const out = noData ? {} : { data: {} };
  for (const [k, v] of Object.entries(obj)) {
    if (DB_MANAGED.has(k)) continue;          // never client-write these
    if (cols.includes(k)) out[k] = v;
    else if (!noData) out.data[k] = v;
  }
  if (isCreate) out.carrier_id = await carrierId();
  return out;
}

const resolveField = (cols, f) => {
  const real = ALIAS[f] || f;
  return cols.includes(real) ? real : `data->>${f}`;
};

function entity(name) {
  const def = TABLES[name];
  if (!def) {
    // Unknown/unsupported entity -- safe no-op.
    return {
      list: async () => [], filter: async () => [], get: async () => null,
      create: async () => { throw new Error(`entity ${name} not mapped`); },
      update: async () => { throw new Error(`entity ${name} not mapped`); },
      delete: async () => ({ success: true }),
      subscribe: () => () => {},   // no-op unsubscribe (see subscribe note below)
    };
  }
  const { table, cols } = def;
  const sel = () => supabase.from(table).select('*');
  const applySort = (q, sort) => {
    if (!sort) return q;
    const desc = sort.startsWith('-');
    const f = resolveField(cols, desc ? sort.slice(1) : sort);
    return q.order(f, { ascending: !desc });
  };
  const run = async (q) => {
    const { data, error } = await q;
    if (error) throw error;
    return (data || []).map(r => fromRow(cols, r));
  };
  return {
    list: (sort, limit) => {
      let q = applySort(sel(), sort);
      if (limit) q = q.limit(limit);
      return run(q);
    },
    filter: (query = {}, sort, limit) => {
      let q = sel();
      for (const [k, v] of Object.entries(query)) {
        if (v === undefined) continue;
        q = q.eq(resolveField(cols, k), v);
      }
      q = applySort(q, sort);
      if (limit) q = q.limit(limit);
      return run(q);
    },
    get: async (id) => {
      const { data, error } = await supabase.from(table).select('*').eq('id', id).maybeSingle();
      if (error) throw error;
      return fromRow(cols, data);
    },
    create: async (obj) => {
      const row = await toRow(cols, obj, { isCreate: true, noData: def.noData });
      const { data, error } = await supabase.from(table).insert(row).select('*').single();
      if (error) throw error;
      return fromRow(cols, data);
    },
    update: async (id, obj) => {
      // PARTIAL update. Split into typed columns vs a jsonb *patch*. The patch
      // MUST be merged into the row's existing `data` — a plain PostgREST
      // .update({data:{...}}) replaces the whole jsonb column and would destroy
      // every untouched key (CDL, banking, equipment, DQF, etc.). Likewise,
      // never send `data` at all on a typed-only update.
      const patch = {};
      const dataPatch = {};
      for (const [k, v] of Object.entries(obj)) {
        if (DB_MANAGED.has(k)) continue;          // never client-write these
        if (cols.includes(k)) patch[k] = v;
        else dataPatch[k] = v;
      }
      if (Object.keys(dataPatch).length) {
        const { data: cur, error: curErr } =
          await supabase.from(table).select('data').eq('id', id).single();
        if (curErr) throw curErr;
        patch.data = { ...(cur?.data || {}), ...dataPatch };  // merge, don't replace
      }
      const { data, error } =
        await supabase.from(table).update(patch).eq('id', id).select('*').single();
      if (error) throw error;
      return fromRow(cols, data);
    },
    delete: async (id) => {
      const { error } = await supabase.from(table).delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    },
    // Realtime subscription. Call sites do:
    //   const unsub = client.entities.X.subscribe(cb); ... unsub()
    //
    // Scoped to the caller's own carrier via the `filter` param below --
    // postgres_changes does NOT enforce RLS on its own, so this filter is
    // what prevents a cross-tenant leak over the realtime channel. Only
    // tables with `alter publication supabase_realtime add table ...` in a
    // migration will actually emit events; subscribing to a table that
    // isn't on the publication is harmless (just never fires).
    //
    // Invalidate-and-refetch design: callback takes no payload, callers
    // just refetch their own query. Simpler and safer than trying to patch
    // individual rows into existing component state.
    subscribe: (cb) => {
      let channel;
      let stopped = false;
      carrierId().then(cid => {
        if (stopped || !cid) return;
        // Random suffix so two components subscribing to the same table
        // at once never collide on channel topic -- Supabase keys
        // channels by topic string, so a second `.channel()` call with
        // an identical topic returns the SAME already-subscribed
        // channel object, and calling `.on()` on it then throws
        // ("cannot add postgres_changes callbacks... after subscribe()").
        channel = supabase
          .channel(`${table}-${cid}-${Math.random().toString(36).slice(2)}`)
          .on('postgres_changes', { event: '*', schema: 'public', table, filter: `carrier_id=eq.${cid}` }, () => cb())
          .subscribe();
      });
      return () => {
        stopped = true;
        if (channel) supabase.removeChannel(channel);
      };
    },
  };
}

const entities = new Proxy({}, { get: (_t, name) => entity(String(name)) });

const auth = {
  me: async () => {
    // getSession(), not getUser(): getUser() always makes a network call
    // with whatever access token is stored, so after the phone app sat in
    // the background (token expired, radio not yet back up) it returned no
    // user and the caller treated that as "logged out" -- forcing a fresh
    // login every time the app was reopened. getSession() reads the stored
    // session locally and refreshes an expired token itself.
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) throw Object.assign(new Error('Not authenticated'), { status: 401 });
    // role mapped from active membership so existing isStaff/isAdmin checks work
    const { data: m } = await supabase.from('memberships')
      .select('role').eq('status', 'active').limit(1).maybeSingle();
    const role = (m?.role === 'owner' || m?.role === 'admin') ? 'admin' : 'user';
    return { id: user.id, email: user.email, full_name: user.user_metadata?.full_name || user.email, role };
  },
  loginViaEmailPassword: async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    _carrierId = undefined; // refresh cache for the new session
    return { success: true };
  },
  logout: async () => { await supabase.auth.signOut(); _carrierId = undefined; },
  setToken: () => {},                 // session handled by supabase-js
  redirectToLogin: () => { window.location.href = '/login'; },
};

const integrations = {
  Core: {
    // NOTE: documents (DL, banking, registration) are PII — must go in a
    // PRIVATE 'documents' bucket (RLS), not the public carrier-logos bucket.
    UploadFile: async ({ file }) => {
      const cid = await carrierId();
      const path = `${cid}/${crypto.randomUUID()}-${file.name}`;
      const { error } = await supabase.storage.from('documents').upload(path, file);
      if (error) throw error;
      const { data } = supabase.storage.from('documents').createSignedUrl
        ? await supabase.storage.from('documents').createSignedUrl(path, 60 * 60 * 24 * 365)
        : { data: null };
      return { file_url: data?.signedUrl || path };
    },
    // Transactional email via the `sendEmail` edge function (Resend).
    // Non-fatal by design at call sites -- they wrap this in try/catch
    // since a driver notification email failing shouldn't block the
    // underlying approval/payment action from completing.
    SendEmail: async ({ to, subject, body, attachments }) => {
      const { data, error } = await supabase.functions.invoke('sendEmail', { body: { to, subject, body, attachments } });
      if (error) throw error;
      return data;
    },
  },
};

// Plain Postgres-RPC passthrough for SECURITY DEFINER functions
// (create_invitation, accept_invitation, revoke_invitation, etc.).
// Errors throw so call sites can `try { await client.rpc(...) }` cleanly.
const rpc = async (name, args) => {
  const { data, error } = await supabase.rpc(name, args);
  if (error) throw error;
  return data;
};

// Edge Functions passthrough. Call sites do:
//   const { url } = await client.functions.invoke('create-checkout-session', { tier: 'starter' })
// supabase-js auto-attaches the user JWT in the Authorization header so the
// function can resolve the caller. Errors throw so call sites use try/catch.
const functions = {
  invoke: async (name, body) => {
    const { data, error } = await supabase.functions.invoke(name, { body });
    if (error) {
      // supabase-js discards the actual response body on any non-2xx
      // status -- `error` is a generic FunctionsHttpError ("Edge Function
      // returned a non-2xx status code") with the real JSON body (our
      // functions always return { error: string } on failure) sitting
      // unparsed on error.context (the raw Response). Without unwrapping
      // it here, every edge-function error surfaced to the app -- and to
      // any user-facing toast/banner built from it -- is this same
      // useless generic string regardless of what actually failed.
      let detail;
      try { detail = (await error.context?.json())?.error; } catch { /* not JSON, or already consumed */ }
      throw new Error(detail || error.message);
    }
    return data;
  },
};

export const client = { entities, auth, integrations, rpc, functions };

// ===================== KNOWN GAPS (next iterations) =====================
// - Legacy `User` entity (old admin user listing) -> no-op; revisit if needed.
// - auth.me() role mapping is approximate (owner/admin -> 'admin'); full
//   per-carrier role model lands in the dedicated auth phase.
// - filter() supports equality only (matches how the app uses it); add
//   range/in operators when a call site needs them.
// - entities.X.subscribe() is real Supabase Realtime now, but only for
//   tables actually added to the supabase_realtime publication via a
//   migration (currently just `loads`, migration 0019). Subscribing to
//   any other table is a harmless no-op until it's added the same way.
